// src/components/resources/tools/generators/BrowserMockup/BrowserMockupGenerator.tsx

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { clsx } from 'clsx'
import { Download, Monitor, RefreshCw, Upload, X, ChevronLeft, ChevronRight, Plus, MoreVertical, Search, Star, Menu, Lock, Shield, Home, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import Image from 'next/image'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'

export type BrowserType = 'chrome' | 'safari' | 'firefox'
export type BrowserTheme = 'light' | 'dark'
export type ViewportPreset = 'hd' | 'laptop' | 'tablet' | 'mobile'

/**
 * Browser mockup configuration interface
 * @interface BrowserMockupConfig
 * @property {string} url - The URL of the website to display in the browser
 * @property {BrowserType} browser - The browser type to use
 * @property {BrowserTheme} theme - The theme of the browser
 * @property {boolean} showShadow - Whether to show the shadow of the browser
 * @property {string} background - The background of the browser
 * @property {string} screenshot - The screenshot of the browser
 */
interface BrowserMockupConfig {
  url: string
  browser: BrowserType
  theme: BrowserTheme
  showShadow: boolean
  background: string
  screenshot?: string
}

const gradientLibrary: string[] = [
  'linear-gradient(120deg, #1f2937, #111827)',
  'linear-gradient(135deg, #1d1f3f, #42275a)',
  'linear-gradient(135deg, #141e30, #243b55)',
  'linear-gradient(135deg, #ff9966, #ff5e62)',
  'linear-gradient(135deg, #00c6ff, #0072ff)',
  'linear-gradient(135deg, #f857a6, #ff5858)',
]

function ChromeBrowser({ config }: { config: BrowserMockupConfig }): JSX.Element {
  const isDark = config.theme === 'dark'
  return (
    <div
      id="browser-mockup-preview"
      className={`w-full max-w-5xl overflow-hidden rounded-t-lg shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
      style={config.showShadow ? { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)' } : undefined}
    >
      {/* Tabs Bar */}
      <div className={`flex items-end gap-0.5 px-2 pt-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className={`flex items-center gap-2 rounded-t-lg px-4 py-2.5 ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-900'}`}>
          <div className="h-4 w-4 rounded bg-blue-500 flex items-center justify-center text-white text-[8px] font-bold">
            A
          </div>
          <span className="text-sm max-w-[180px] truncate">AdSights Platform</span>
          <button className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-0.5">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className={`flex items-center gap-2 rounded-t-lg px-4 py-2 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
          <span className="text-sm">New Tab</span>
        </div>
        <button className={`p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-t ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Address Bar */}
      <div className={`flex items-center gap-3 px-3 py-2.5 border-b ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-1">
          <button className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <ChevronRight className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        
        <div className={`flex flex-1 items-center gap-2 rounded-full px-4 py-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <Lock className={`h-3.5 w-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          <span className={`text-sm flex-1 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{config.url}</span>
          <Star className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>

        <div className="flex items-center gap-1">
          <button className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Shield className="h-4 w-4" />
          </button>
          <button className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="h-7 w-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold ml-1">
            U
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className={`${isDark ? 'bg-gray-950' : 'bg-white'} p-6`}>
        {config.screenshot ? (
          <Image
            src={config.screenshot}
            alt="Browser content"
            className="w-full rounded-lg shadow-lg"
            width={1200}
            height={800}
            unoptimized
            priority
          />
        ) : (
          <div className={`flex h-96 flex-col items-center justify-center rounded-lg border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-900 text-gray-400' : 'border-gray-300 bg-gray-50 text-gray-500'}`}>
            <Monitor className="h-12 w-12" />
            <p className="mt-4 text-sm font-medium">Upload a screenshot to preview</p>
          </div>
        )}
      </div>
    </div>
  )
}

function SafariBrowser({ config }: { config: BrowserMockupConfig }): JSX.Element {
  const isDark = config.theme === 'dark'
  return (
    <div
      id="browser-mockup-preview"
      className={`w-full max-w-5xl overflow-hidden rounded-lg shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
      style={config.showShadow ? { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)' } : undefined}
    >
      {/* Unified Toolbar */}
      <div className={`flex items-center gap-3 px-3 py-2.5 border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <div className="flex gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500"></div>
            <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
            <div className="h-3 w-3 rounded-full bg-green-500"></div>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className={`flex flex-1 items-center gap-2 rounded-lg px-3 py-1.5 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
          <Search className={`h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <span className={`text-sm flex-1 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{config.url}</span>
          <RefreshCw className={`h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Share2 className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Home className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tab Bar (below toolbar in Safari). `bg-gray-800`, not `bg-gray-850`:
          850 isn't in the palette (and isn't extended in tailwind.config), so
          the old class was purged and the dark-mode tab bar rendered with NO
          background. This is a real (intended) visual fix, not a no-op. */}
      <div className={`flex items-center gap-1 px-3 py-1.5 border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
        <div className={`flex items-center gap-2 rounded-md px-3 py-1.5 ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-900 shadow-sm'}`}>
          <div className="h-3.5 w-3.5 rounded bg-blue-500"></div>
          <span className="text-xs max-w-[160px] truncate">AdSights</span>
          <button className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-0.5">
            <X className="h-3 w-3" />
          </button>
        </div>
        <div className={`flex items-center gap-2 rounded-md px-3 py-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
          <span className="text-xs">New Tab</span>
        </div>
        <button className={`p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded ml-auto ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Content Area */}
      <div className={`${isDark ? 'bg-gray-950' : 'bg-white'} p-6`}>
        {config.screenshot ? (
          <Image
            src={config.screenshot}
            alt="Browser content"
            className="w-full rounded-lg shadow-lg"
            width={1200}
            height={800}
            unoptimized
            priority
          />
        ) : (
          <div className={`flex h-96 flex-col items-center justify-center rounded-lg border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-900 text-gray-400' : 'border-gray-300 bg-gray-50 text-gray-500'}`}>
            <Monitor className="h-12 w-12" />
            <p className="mt-4 text-sm font-medium">Upload a screenshot to preview</p>
          </div>
        )}
      </div>
    </div>
  )
}

function FirefoxBrowser({ config }: { config: BrowserMockupConfig }): JSX.Element {
  const isDark = config.theme === 'dark'
  return (
    <div
      id="browser-mockup-preview"
      className={`w-full max-w-5xl overflow-hidden rounded-t-lg shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
      style={config.showShadow ? { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)' } : undefined}
    >
      {/* Tabs Bar */}
      <div className={`flex items-end px-3 pt-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <button className={`p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded mr-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <Menu className="h-4 w-4" />
        </button>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-t-xl ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-900'}`}>
          <div className="h-4 w-4 rounded bg-orange-500 flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-white"></div>
          </div>
          <span className="text-sm max-w-[180px] truncate">AdSights Platform</span>
          <button className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-0.5">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-t-xl ml-1 ${isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-600'}`}>
          <span className="text-sm">New Tab</span>
        </div>
        <button className={`p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded ml-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Address Bar */}
      <div className={`flex items-center gap-2 px-3 py-2.5 border-b ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-1">
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <ChevronRight className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <RefreshCw className="h-4 w-4" />
          </button>
          <button className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Home className="h-4 w-4" />
          </button>
        </div>
        
        <div className={`flex flex-1 items-center gap-2 rounded px-3 py-2 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-300'}`}>
          <Lock className={`h-3.5 w-3.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
          <span className={`text-sm flex-1 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{config.url}</span>
          <Star className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>

        <div className="flex items-center gap-1">
          <button className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Shield className="h-4 w-4" />
          </button>
          <button className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-800 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className={`${isDark ? 'bg-gray-950' : 'bg-white'} p-6`}>
        {config.screenshot ? (
          <Image
            src={config.screenshot}
            alt="Browser content"
            className="w-full rounded-lg shadow-lg"
            width={1200}
            height={800}
            unoptimized
            priority
          />
        ) : (
          <div className={`flex h-96 flex-col items-center justify-center rounded-lg border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-900 text-gray-400' : 'border-gray-300 bg-gray-50 text-gray-500'}`}>
            <Monitor className="h-12 w-12" />
            <p className="mt-4 text-sm font-medium">Upload a screenshot to preview</p>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Props accepted by {@link BrowserMockupGenerator}. All optional so the
 * umbrella route (no preset) and the per-browser variant routes (Chrome /
 * Firefox / Safari) share one component.
 */
interface BrowserMockupGeneratorProps {
  /**
   * Browser chrome to boot into. Variant routes pass this so the page renders
   * in the right frame on first paint; the umbrella route omits it and
   * defaults to Chrome.
   */
  initialBrowser?: BrowserType
  /**
   * Analytics slug override so variant routes attribute engagement / download
   * events to their own slug instead of the umbrella default.
   */
  trackingSlug?: string
}

export function BrowserMockupGenerator({
  initialBrowser,
  trackingSlug,
}: BrowserMockupGeneratorProps = {}): JSX.Element {
  const trackingResourceSlug = trackingSlug ?? 'browser-mockup-generator'
  const [config, setConfig] = useState<BrowserMockupConfig>({
    url: 'https://app.adsights.ai/campaigns/performance',
    browser: initialBrowser ?? 'chrome',
    theme: 'light',
    showShadow: true,
    background: gradientLibrary[1] ?? 'linear-gradient(135deg, #1d1f3f, #42275a)',
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewContainerRef = useRef<HTMLDivElement>(null)

  // Shared export lifecycle: warms html2canvas on mount, a synchronous
  // re-entrancy guard, and the busy flag. `exportingFormat` tracks WHICH of the
  // two formats is capturing so only the clicked button shows the spinner while
  // both are disabled (the guard already blocks a concurrent capture).
  const { isExporting, runExport } = useExport()
  const [exportingFormat, setExportingFormat] = useState<'png' | 'webp' | null>(null)

  // Defer the browser-chrome preview so URL keystrokes commit at the controls
  // immediately while the heavy chrome (and any uploaded screenshot) re-renders
  // at a lower priority. `previewStaleRef` (committed from an effect) gates export
  // so the PNG matches the live controls. See `usePreviewDeferral`.
  const { deferred: deferredConfig, isStale } = usePreviewDeferral(config)
  const previewStaleRef = useStaleRef(isStale)

  // Analytics: Create engagement timers once and keep them stable
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  // Local image upload, client-side so no need to use UploadThing
  const handleFileUpload = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file.')
      return
    }

    const reader = new FileReader()
    reader.onload = e => {
      setConfig(prev => ({ ...prev, screenshot: e.target?.result as string }))
      
      // Track image upload - user uploaded a screenshot to use in browser mockup
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingResourceSlug,
        engagementMs: getDeltaEngagementMs(),
        params: {
          tool_type: 'generator',
          interaction_type: 'image_upload',
          file_type: file.type,
          file_size: file.size
        }
      })
    }
    reader.readAsDataURL(file)
  }, [getDeltaEngagementMs, trackingResourceSlug])

  const cycleBackground = useCallback((): void => {
    const nextGradient = gradientLibrary[Math.floor(Math.random() * gradientLibrary.length)]
    if (nextGradient) {
      setConfig(prev => ({ ...prev, background: nextGradient }))
    }
  }, [])

  // Download logic using html2canvas. Supports PNG (lossless, maximum
  // compatibility) and WebP (smaller files at comparable quality) so the
  // rendered surface matches the two download buttons advertised in
  // `browserMockupGenerator.ts` (featureList + FAQ + output.variants).
  const downloadImage = useCallback((format: 'png' | 'webp'): Promise<void> => runExport(async beginCapture => {
    // Settle FIRST (a short wait for any uploaded-image paint), THEN gate on
    // stability — so the stability check is the last async step before the
    // synchronous node read + capture. Ordering it the other way (gate → settle)
    // leaves a 200ms window in which a stray control edit advances `config`, the
    // deferred preview goes stale again, and the capture reads a mid-update DOM —
    // exactly the stale frame the gate exists to prevent.
    await new Promise(resolve => setTimeout(resolve, 200))

    // The preview renders from `deferredConfig`; wait for that snapshot to
    // converge. Switching browser chrome (Chrome↔Safari↔Firefox) unmounts the old
    // `#browser-mockup-preview` node and mounts a new one on the deferred commit,
    // so we resolve the node AFTER the wait — resolving it before would capture a
    // detached, stale (or zero-size) subtree. Fail loudly if it can't settle.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then download again.')
      return
    }

    let element: HTMLElement | null = document.getElementById('browser-mockup-preview')
    if (!element && previewContainerRef.current) {
      element = previewContainerRef.current.querySelector<HTMLElement>('#browser-mockup-preview')
    }
    if (!element && previewContainerRef.current?.firstElementChild) {
      element = previewContainerRef.current.firstElementChild as HTMLElement
    }
    if (!element) {
      toast.error('Preview not available. Please ensure the preview is visible before downloading.')
      return
    }

    // Capture is starting — surface the busy state on the clicked format's
    // button so the multi-second html2canvas block isn't silent.
    setExportingFormat(format)
    beginCapture()
    try {
      await captureAndDownload({
        element,
        format,
        filename: `browser-mockup-${new Date().toISOString().split('T')[0]}`,
        trackingSlug: trackingResourceSlug,
        engagementMs: getTotalEngagementMs(),
        onError: () =>
          toast.error(`Failed to generate ${format.toUpperCase()}. Please try again.`),
      })
    } finally {
      setExportingFormat(null)
    }
  }), [runExport, getTotalEngagementMs, trackingResourceSlug, previewStaleRef])

  const preview = useMemo<JSX.Element>((): JSX.Element => {
    if (deferredConfig.browser === 'chrome') return <ChromeBrowser config={deferredConfig} />
    if (deferredConfig.browser === 'safari') return <SafariBrowser config={deferredConfig} />
    return <FirefoxBrowser config={deferredConfig} />
  }, [deferredConfig])

  return (
    <div className="flex w-full flex-col gap-10 lg:flex-row">
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 lg:w-[420px]">
        <section aria-labelledby="browser-selection" className="space-y-3">
          <h2 id="browser-selection" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Browser
          </h2>
          <div className="grid grid-cols-3 gap-3 text-sm">
            {(['chrome', 'safari', 'firefox'] as BrowserType[]).map((browser: BrowserType) => (
              <button
                key={browser}
                type="button"
                className={clsx(
                  'rounded-xl border px-3 py-2 font-medium capitalize transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  config.browser === browser
                    ? 'border-transparent bg-slate-900 text-white shadow-lg dark:bg-indigo-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                )}
                onClick={() => setConfig(prev => ({ ...prev, browser }))}
                aria-pressed={config.browser === browser}
              >
                {browser}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <span className="font-medium text-slate-700 dark:text-slate-100">Theme</span>
            <button
              type="button"
              onClick={() => setConfig(prev => ({ ...prev, theme: prev.theme === 'dark' ? 'light' : 'dark' }))}
              className="rounded-lg bg-slate-900 px-3 py-1 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              {config.theme === 'dark' ? 'Dark' : 'Light'} mode
            </button>
          </div>
        </section>

        <section aria-labelledby="appearance" className="space-y-3">
          <h2 id="appearance" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Appearance
          </h2>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-200">Address bar URL</span>
            <input
              type="text"
              value={config.url}
              onChange={event => setConfig(prev => ({ ...prev, url: event.target.value }))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-200">Background</span>
            <input
              type="text"
              value={config.background}
              onChange={event => setConfig(prev => ({ ...prev, background: event.target.value }))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>
          <div className="grid grid-cols-3 gap-3">
            {gradientLibrary.map(gradient => (
              <button
                key={gradient}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, background: gradient }))}
                className="h-14 rounded-2xl border border-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700"
                style={{ background: gradient }}
              >
                <span className="sr-only">Select gradient</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={cycleBackground}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            <RefreshCw className="h-4 w-4" />
            Shuffle gradient
          </button>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.showShadow}
                onChange={event => setConfig(prev => ({ ...prev, showShadow: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-indigo-500 dark:focus:ring-indigo-500"
              />
              Drop shadow
            </label>
          </div>
        </section>

        <section aria-labelledby="asset" className="space-y-3">
          <h2 id="asset" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Artwork
          </h2>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          >
            <Upload className="h-4 w-4" />
            Upload screenshot
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Use your captured UI or marketing landing page. For transparent PNGs, the gradient background will shine through.
          </p>
        </section>

        <section aria-labelledby="export" className="space-y-3">
          <h2 id="export" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Export as PNG for maximum compatibility, or WebP for smaller files at comparable quality — for presentations, decks, and storefront screenshots.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <ExportButton
              isExporting={exportingFormat === 'png'}
              disabled={isExporting}
              onExport={() => void downloadImage('png')}
              icon={<Download className="h-4 w-4" />}
              className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            />
            <ExportButton
              isExporting={exportingFormat === 'webp'}
              disabled={isExporting}
              onExport={() => void downloadImage('webp')}
              icon={<Download className="h-4 w-4" />}
              label="Download WebP"
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>
        </section>
      </aside>

      <div
        className="flex flex-1 items-center justify-center rounded-3xl border border-white/10 p-10 shadow-2xl"
        style={{ background: deferredConfig.background }}
        ref={previewContainerRef}
      >
        {preview}
      </div>
    </div>
  )
}
